import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';

import { environment } from '../../../environments/environment';
import { ApiPageResponse, PageQuery } from '../types/api.types';
import { Vehicle, VehicleStatus } from '../types/vehicle.types';

@Injectable({ providedIn: 'root' })
export class VehiclesService {
  private readonly http = inject(HttpClient);
  private readonly base = `${environment.gatewayUrl}/rental/api/vehicles`;

  list(query: PageQuery = {}): Observable<ApiPageResponse<Vehicle>> {
    return this.http.get<ApiPageResponse<Vehicle>>(this.base, {
      params: this.pageParams(query),
    });
  }

  pendingReview(query: PageQuery = {}): Observable<ApiPageResponse<Vehicle>> {
    let params = new HttpParams()
      .set('pageable', 'true')
      .set('page', String(query.page ?? 1))
      .set('size', String(query.size ?? 10));
    return this.http.get<ApiPageResponse<Vehicle>>(`${this.base}/pending-review`, { params });
  }

  get(id: number): Observable<Vehicle> {
    return this.http.get<Vehicle>(`${this.base}/${id}`);
  }

  delete(id: number): Observable<void> {
    return this.http.delete<void>(`${this.base}/${id}`);
  }

  approve(id: number): Observable<Vehicle> {
    return this.http.post<Vehicle>(`${this.base}/${id}/approve`, {});
  }

  reject(id: number, reason: string): Observable<Vehicle> {
    return this.http.post<Vehicle>(`${this.base}/${id}/reject`, { reason });
  }

  /** Curation admin (§2.3a) — épingle/retire « à la une » :
   *  `PATCH /vehicles/{id}/featured?value=`. Renvoie le véhicule à jour. */
  setFeatured(id: number, value: boolean): Observable<Vehicle> {
    const params = new HttpParams().set('value', String(value));
    return this.http.patch<Vehicle>(`${this.base}/${id}/featured`, null, { params });
  }

  /** Triggers a full TypeSense reindex (admin maintenance). */
  reindex(): Observable<unknown> {
    return this.http.post(`${this.base}/search/reindex`, {});
  }

  count(search?: string): Observable<number> {
    let params = new HttpParams();
    if (search?.trim()) params = params.set('search', search.trim());
    return this.http.get<number>(`${this.base}/count`, { params });
  }

  private pageParams(query: PageQuery): HttpParams {
    let params = new HttpParams()
      .set('pageable', 'true')
      .set('page', String(query.page ?? 1))
      .set('size', String(query.size ?? 10))
      .set('isDeleted', String(query.isDeleted ?? false));
    if (query.search?.trim()) params = params.set('search', query.search.trim());
    return params;
  }
}
